import type { TFunction } from 'i18next'

import type { GreskaPolja } from '../i18n/greske'

/**
 * ZAKON FORME (dotjerivanje-10 §1) — **submit se NIKAD tiho ne guta**.
 *
 * Terenski nalaz 12.08.2026., i to najgore vrste: vlasnik je na recepciji unio
 * novu najavu ("Toni Zetović"), pritisnuo "Spremi" i otišao dalje. Zahtjev NIKAD
 * NIJE OTIŠAO s ekrana — u API logu nema POST-a. Recepcija je mislila da je gost
 * najavljen, a gost u sustavu nije postojao: tihi gubitak podataka, jedina vrsta
 * greške koju nitko ne prijavi jer je nitko ne vidi.
 *
 * **Kako se submit uspije izgubiti.** Obrazac je stajao na nativnoj provjeri
 * (`required`, `min`/`max`, `type="date"`). Kad polje ne prođe, preglednik NE
 * pošalje `submit` — a oblačić uz polje pokaže samo ako ga ima gdje i kome
 * pokazati: kontrola koja je u pomičnom dijelu `<dialog>`-a, iza ruba ili
 * nedostupna fokusu dobiva u Chromeu tišinu i redak u konzoli. Dodaj tomu datum
 * upisan tipkovnicom u obliku koji polje ne prima (Windows regionalne postavke
 * preglednik ionako ne čita) — vrijednost ostane PRAZNA, `badInput` upaljen, i
 * gumb prestane raditi bez ijedne riječi.
 *
 * **Zato provjeru vodimo SAMI, na jednom mjestu, i ona uvijek nešto kaže:**
 *
 * 1. obrazac nosi `noValidate` — `submit` se dogodi UVIJEK, pa nema tihog kraja;
 * 2. <see cref="provjeriObrazac"/> pročita `validity` svake kontrole i vrati
 *    popis onoga što je palo — **koje polje i zašto**, ljudskim jezikom (isti
 *    oblik kojim se razlaže 422 s poslužitelja, `i18n/greske.ts`);
 * 3. obrazac dobiva `data-provjeren="da"`, pa CSS crveno obrubi svako nevaljano
 *    polje (`styles/index.css`) — vidljiva oznaka UZ polje, ne samo rečenica;
 * 4. prvo palo polje dobiva fokus (i skrola se u vidljivo), jer je u dugom
 *    obrascu "gdje je to" drugo pitanje nakon "što je palo";
 * 5. **uspjeh se potvrdi** — obrazac koji nestane bez riječi izgleda isto kao
 *    obrazac koji se izgubio (`Potvrda`, `components/Layoutovi.tsx`);
 * 6. 422 s poslužitelja stoji uz polja (`porukaPolja`) i u sažetku
 *    (`sazetakPolja`), u istom popisu kao i klijentska provjera.
 *
 * Zakon vrijedi za SVE obrasce: `FormModal` (konzola, sys, recepcija, admin) i
 * `Obrazac` (gost i vozač, noir) su jedina dva mjesta koja ga izvode, pa nijedan
 * ekran ne sudi sam.
 */

/** Jedno polje koje nije prošlo provjeru PREGLEDNIKA, prije ijednog poziva. */
export interface NevaljanoPolje {
  /** `name` ili `id` kontrole — ključ popisa, ne tekst za čovjeka. */
  polje: string
  /** Natpis s ekrana, bez zvjezdice obaveznosti. */
  oznaka?: string
  /** Ključ pravila (`validation.*`) koje je palo. */
  kljuc: string
}

/** Oznaka na obrascu koji je provjeru PROŠAO kroz ruke; CSS po njoj crta rub. */
export const OZNAKA_PROVJERE = 'data-provjeren'

type Kontrola = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

function jeKontrola(element: Element): element is Kontrola {
  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLSelectElement ||
    element instanceof HTMLTextAreaElement
  )
}

/**
 * Zašto je polje palo — jedan ključ po pravilu.
 *
 * **`badInput` se pita PRVI, i to je cijeli nalaz Toni slučaja**: datum upisan u
 * obliku koji polje ne prima ima praznu vrijednost, pa bi ga "polje je obavezno"
 * poslalo da upiše ono što je već upisao. Rečenica mora reći da oblik nije
 * pročitan i da datum ide kroz kalendar.
 */
function razlog(kontrola: Kontrola): string {
  const stanje = kontrola.validity
  const vrsta = kontrola instanceof HTMLInputElement ? kontrola.type : ''
  const satni = vrsta === 'date' || vrsta === 'time' || vrsta === 'datetime-local'

  if (stanje.badInput) {
    if (satni) return 'unos_datum'

    return vrsta === 'number' ? 'unos_broj' : 'unos_oblik'
  }

  if (stanje.valueMissing) return satni ? 'unos_datum_obavezno' : 'unos_obavezno'
  if (stanje.typeMismatch) return vrsta === 'email' ? 'email_invalid' : 'unos_oblik'
  if (stanje.rangeUnderflow || stanje.rangeOverflow || stanje.stepMismatch) return 'unos_raspon'
  if (stanje.tooLong || stanje.tooShort) return 'unos_duzina'
  if (stanje.patternMismatch) return 'unos_oblik'

  return 'unknown'
}

/**
 * Natpis polja s EKRANA, jer je to jedina riječ koju korisnik za to polje zna.
 *
 * `labels` je prvi izvor (jsdom ga zna kao i preglednik); rezerva je natpis u
 * istom okviru, za kontrolu bez `id`-a. Zvjezdica obaveznosti se skida — ona je
 * oznaka, ne dio imena.
 */
function oznakaPolja(kontrola: Kontrola): string | undefined {
  const natpis = kontrola.labels?.[0] ?? kontrola.closest('div')?.querySelector('label') ?? null
  const tekst = natpis?.textContent?.replace(/\*/g, '').trim()

  if (tekst && tekst.length > 0) return tekst

  // Kontrola BEZ natpisa nije bezimena ako ime nosi sama. To je slučaj vlastitog
  // pickera (D15-A §4): ondje natpis stoji nad GUMBOM, a obaveznost čuva odrezano
  // polje pored njega — koje je iz stabla pristupačnosti namjerno vani, pa mu ime
  // stoji kao podatak.
  const vlastito = (kontrola.dataset.oznaka ?? kontrola.getAttribute('aria-label'))?.trim()

  return vlastito && vlastito.length > 0 ? vlastito : undefined
}

/**
 * Provjeri obrazac i OZNAČI ga: vraća popis onoga što je palo (prazan kad je sve
 * u redu), upiše `data-provjeren` i fokusira prvo palo polje.
 *
 * Nativna provjera se ovime ne dopunjuje nego ZAMJENJUJE — obrazac koji ovo zove
 * nosi `noValidate`, inače bi se preglednik javio prvi i opet mogao zaboraviti.
 */
export function provjeriObrazac(forma: HTMLFormElement): NevaljanoPolje[] {
  const pala: NevaljanoPolje[] = []
  let prvo: Kontrola | null = null

  for (const element of Array.from(forma.elements)) {
    if (!jeKontrola(element)) continue

    // `willValidate` je `false` za onemogućena, skrivena i `readonly` polja —
    // njih ni ne pitamo: polje koje korisnik ne može promijeniti ne smije mu
    // zaustaviti obrazac.
    if (!element.willValidate || element.checkValidity()) continue

    if (!prvo) prvo = element

    pala.push({
      polje: element.name || element.id,
      oznaka: oznakaPolja(element),
      kljuc: razlog(element),
    })
  }

  forma.setAttribute(OZNAKA_PROVJERE, pala.length > 0 ? 'da' : 'ne')

  if (prvo) {
    // Fokus je dio poruke: u obrascu s deset polja rečenica bez skoka na polje
    // je zagonetka. `scrollIntoView` stoji uz fokus jer polje u pomičnom dijelu
    // dijaloga fokus dobije, a da se ne pomakne u vidljivo.
    //
    // KONTROLA SMIJE POKAZATI NA DRUGU (D15-A §4): brana obaveznosti vlastitog
    // pickera je odrezano polje koje se ne vidi, pa bi fokus na njemu bio fokus
    // niotkuda. `data-fokus` nosi id onoga što korisnik doista gleda.
    const cilj = prvo.dataset.fokus
    const vidljivo = cilj ? forma.ownerDocument.getElementById(cilj) : null
    const meta = vidljivo ?? prvo

    meta.focus()
    meta.scrollIntoView?.({ block: 'center' })
  }

  return pala
}

/**
 * Popis palih pravila u OBLIKU U KOJEM SE VEĆ ISPISUJE 422 (`GreskaPolja`):
 * jedan popis, jedan izgled, jedno mjesto na ekranu — bez obzira je li odbio
 * preglednik ili poslužitelj.
 */
export function zakonUPoruke(t: TFunction, pala: readonly NevaljanoPolje[]): GreskaPolja[] {
  return pala.map((stavka) => ({
    polje: stavka.polje,
    oznaka: stavka.oznaka,
    poruka: t(`validation.${stavka.kljuc}`, { defaultValue: t('validation.unknown') }),
  }))
}
